// @ts-nocheck
/**
 * ツインキーシステム - スキル解決ロジック
 *
 * カード情報からスキルセットを決定する
 * 優先順位: 固有 → 役割 → 属性
 *
 * 仕様書: twin_key_generic_skills_passives_v1.md
 */

import { getRoleCategory, getCostBand, COST_BANDS } from '../categoryMapping';
import { getAttributeSkills } from './attributeSkills';
import { getRoleSkills } from './roleSkills';
import { getUniqueSkills, hasUniqueSkills } from './uniqueSkills';

// ========================================
// スキル解決メイン関数
// ========================================

/**
 * カード情報からスキルセットを決定
 *
 * 優先順位:
 * 1. 固有スキル持ち → 固有スキルを使用
 * 2. 役割カテゴリ持ち → 役割スキルを使用
 * 3. それ以外 → 属性スキルを使用
 *
 * @param {Object} card - カードデータ
 * @param {string} card.id - カードID
 * @param {string} card.attribute - 属性
 * @param {string} card.category - カテゴリ
 * @param {number|string} card.cost - コスト
 * @returns {Object} スキル解決結果
 */
export const resolveSkills = (card) => {
  if (!card) {
    return {
      skills: [],
      source: 'none',
      sourceDetail: null,
    };
  }

  const { id, attribute, category, cost } = card;
  const costBand = getCostBand(cost);

  // 1. 固有スキルチェック
  if (hasUniqueSkills(id)) {
    const uniqueSkills = getUniqueSkills(id);
    return {
      skills: uniqueSkills,
      source: 'unique',
      sourceDetail: { cardId: id },
    };
  }

  // 2. 役割カテゴリチェック
  const roleCategory = getRoleCategory(category);
  if (roleCategory) {
    const roleSkills = getRoleSkills(roleCategory, costBand);
    if (roleSkills) {
      return {
        skills: roleSkills,
        source: 'role',
        sourceDetail: { role: roleCategory, costBand },
      };
    }
  }

  // 3. 属性スキル（デフォルト）
  const attributeSkills = getAttributeSkills(attribute, costBand);
  return {
    skills: attributeSkills,
    source: 'attribute',
    sourceDetail: { attribute, costBand },
  };
};

/**
 * スキル解決結果からスキル配列のみを取得
 * @param {Object} card - カードデータ
 * @returns {Object[]} スキル配列
 */
export const getSkillsForCard = (card) => {
  const result = resolveSkills(card);
  return result.skills;
};

/**
 * カードのスキルソース情報を取得
 * UIでスキルの由来を表示する際に使用
 * @param {Object} card - カードデータ
 * @returns {Object} { source, sourceDetail }
 */
export const getSkillSource = (card) => {
  const result = resolveSkills(card);
  return {
    source: result.source,
    sourceDetail: result.sourceDetail,
  };
};

/**
 * スキルソースの表示名を取得
 * @param {string} source - スキルソース
 * @param {Object} sourceDetail - ソース詳細
 * @returns {string} 表示名
 */
export const getSkillSourceDisplayName = (source, sourceDetail) => {
  switch (source) {
    case 'unique':
      return '固有スキル';
    case 'role':
      const roleNames = {
        healer: 'ヒーラー',
        tank: 'タンク',
        speed: 'スピード',
      };
      return `${roleNames[sourceDetail.role] || sourceDetail.role}スキル`;
    case 'attribute':
      return `${sourceDetail.attribute}属性スキル`;
    default:
      return '汎用スキル';
  }
};

// ========================================
// スキルレベル・解放判定
// ========================================

/**
 * スキル解放レベル定義
 */
export const SKILL_UNLOCK_LEVELS = {
  1: 20,  // スキル1: Lv.20
  2: 40,  // スキル2: Lv.40
  3: 60,  // スキル3: Lv.60（必殺技）
};

/**
 * レベルに応じたスキル解放状態を取得
 * @param {number} level - 現在レベル
 * @returns {Object} { skill1: boolean, skill2: boolean, skill3: boolean }
 */
export const getSkillUnlockStatus = (level) => {
  return {
    skill1: level >= SKILL_UNLOCK_LEVELS[1],
    skill2: level >= SKILL_UNLOCK_LEVELS[2],
    skill3: level >= SKILL_UNLOCK_LEVELS[3],
  };
};

/**
 * スキルが解放されているか確認
 * @param {number} skillSlot - スキルスロット（1-3）
 * @param {number} level - 現在レベル
 * @returns {boolean} 解放されていればtrue
 */
export const isSkillUnlocked = (skillSlot, level) => {
  const unlockLevel = SKILL_UNLOCK_LEVELS[skillSlot];
  return level >= unlockLevel;
};

/**
 * 次のスキル解放レベルを取得
 * @param {number} level - 現在レベル
 * @returns {number|null} 次の解放レベル、全解放済みならnull
 */
export const getNextSkillUnlockLevel = (level) => {
  for (const unlockLevel of Object.values(SKILL_UNLOCK_LEVELS)) {
    if (level < unlockLevel) {
      return unlockLevel;
    }
  }
  return null;
};

// ========================================
// スキルインスタンス生成
// ========================================

/**
 * モンスターインスタンス用のスキル配列を生成
 * 解放状態とクールダウンを含む
 *
 * @param {Object} card - カードデータ
 * @param {number} level - 現在レベル
 * @returns {Object[]} スキルインスタンス配列
 */
export const createSkillInstances = (card, level) => {
  const { skills } = resolveSkills(card);
  const unlockStatus = getSkillUnlockStatus(level);

  return skills.map((skill, index) => {
    const slotNumber = index + 1;
    const unlocked = unlockStatus[`skill${slotNumber}`];

    return {
      ...skill,
      slotNumber,
      unlocked,
      currentCooldown: 0,
      initialCooldown: skill.initialCooldown || 0,
    };
  });
};

/**
 * 戦闘開始時のスキル状態を初期化
 * 必殺技（スキル3）は初期クールダウンを設定
 *
 * @param {Object[]} skillInstances - スキルインスタンス配列
 * @returns {Object[]} 初期化されたスキルインスタンス配列
 */
export const initializeSkillsForBattle = (skillInstances) => {
  return skillInstances.map(skill => ({
    ...skill,
    currentCooldown: skill.slotNumber === 3 ? (skill.initialCooldown || 3) : 0,
  }));
};

/**
 * スキル使用後のクールダウンを更新
 * @param {Object} skill - スキルオブジェクト
 * @returns {Object} 更新されたスキルオブジェクト
 */
export const applySkillCooldown = (skill) => {
  return {
    ...skill,
    currentCooldown: skill.cooldown,
  };
};

/**
 * ターン終了時のクールダウン減少
 * @param {Object} skill - スキルオブジェクト
 * @returns {Object} 更新されたスキルオブジェクト
 */
export const tickSkillCooldown = (skill) => {
  if (skill.currentCooldown <= 0) return skill;

  return {
    ...skill,
    currentCooldown: Math.max(0, skill.currentCooldown - 1),
  };
};

/**
 * スキルが使用可能か確認
 * @param {Object} skill - スキルオブジェクト
 * @returns {boolean} 使用可能ならtrue
 */
export const isSkillReady = (skill) => {
  return skill.unlocked && skill.currentCooldown === 0;
};

// ========================================
// エクスポート
// ========================================

export default {
  resolveSkills,
  getSkillsForCard,
  getSkillSource,
  getSkillSourceDisplayName,
  SKILL_UNLOCK_LEVELS,
  getSkillUnlockStatus,
  isSkillUnlocked,
  getNextSkillUnlockLevel,
  createSkillInstances,
  initializeSkillsForBattle,
  applySkillCooldown,
  tickSkillCooldown,
  isSkillReady,
};
